import { useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useFirestore, useFirestoreDocDataOnce, useUser } from 'reactfire';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { doc, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { CircularProgress, Grid2 as Grid } from '@mui/material';
import { isEqual } from 'es-toolkit';
import dayjs from 'dayjs';

import AuthDialog from './AuthDialog';
import CodePanel from './CodePanel';
import MessagePanel from './MessagePanel';
import PromptToolbar from './PromptToolbar';
import ChangeRequestDialog from './changeRequest/ChangeRequestDialog';
import {
  ChatPageContainer,
  ChatsContainer,
  GridContainer,
  Progress,
} from '../../styled/Chat.styled';
import { LoaderContainer } from '../../styled/App.styled';

import { useChat } from '../../hooks/useChat';
import { ChatMessage } from '../../types';

export const initialMessages: ChatMessage[] = [
  {
    kind: 'BOT_MESSAGE',
    text: 'Як я можу Вам допомогти?',
  },
  {
    kind: 'BOT_MESSAGE',
    text: 'Дайте мені будь-яку задачу, пов\'язану з написанням коду. Наприклад: "Напиши простий веб-сайт"',
  },
];

export const initialSuggestions = [
  'Створити лендінг',
  'Створити мобільний застосунок',
  'Створити веб-сайт',
  'Створити API',
];

const CHAT_LIFETIME_MINUTES = 20;

const Chat = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [minutesSinceChatCreated, setMinutesSinceChatCreated] = useState(0);
  const [changeRequestDialogOpen, setChangeRequestDialogOpen] = useState(false);

  const conversationMessagesReceivedRef = useRef(false);
  const previousSessionId = useRef<string | null>(null);

  const firestore = useFirestore();
  const { sessionId, userUid } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const { data: user } = useUser();

  const uid = userUid ?? user?.uid;

  const {
    conversationId,
    messages,
    setMessages,
    suggestions,
    setSuggestions,
    code,
    setCode,
    status,
    sendPrompt,
  } = useChat({
    initialMessages,
    initialSuggestions,
    sessionId: sessionId ?? null,
    isAuthenticated: !!user,
  });

  const promptDisabled =
    status.isPending ||
    status.isRejected ||
    minutesSinceChatCreated > CHAT_LIFETIME_MINUTES;

  const { status: conversationStatus, data: conversation } =
    useFirestoreDocDataOnce(
      doc(firestore, `chats/${uid}/conversations/${sessionId}`),
      {
        idField: 'id',
        initialData: {
          empty: true,
        },
      },
    );

  const resetChat = () => {
    setMessages(initialMessages);
    setSuggestions(initialSuggestions);
    setCode(null);
    setMinutesSinceChatCreated(0);

    conversationMessagesReceivedRef.current = true;
  };

  useEffect(() => {
    if (!sessionId || !user) {
      resetChat();

      return;
    }

    if (!uid) return;

    previousSessionId.current = sessionId;
  }, [user, sessionId, uid]);

  useEffect(() => {
    if (user && conversationId) navigate(`/${conversationId}/${uid}`);
  }, [user, conversationId]);

  useEffect(() => {
    if (conversation && conversation.empty) {
      return;
    }

    if (!conversation) {
      navigate('/', { replace: true });
      return;
    }

    const now = dayjs();

    setMessages(conversation.messages || initialMessages);
    setSuggestions(conversation.suggestions || initialSuggestions);
    setCode(conversation.code || null);

    if (conversation.createdAt) {
      setMinutesSinceChatCreated(
        now.diff(conversation.createdAt.toDate(), 'minute'),
      );
    }

    conversationMessagesReceivedRef.current = true;
  }, [conversation, conversationStatus]);

  const updateConversation = async () => {
    if (!sessionId && previousSessionId.current === conversationId) return;

    if (location.pathname !== '/' && !conversation) return;

    if (
      conversation &&
      (isEqual(messages, initialMessages) ||
        isEqual(messages, conversation.messages))
    ) {
      return;
    }

    try {
      const conversationRef = doc(
        firestore,
        `chats/${uid}/conversations/${conversationId}`,
      );

      if (conversation) {
        await updateDoc(conversationRef, {
          messages,
          suggestions,
          code,
        });
      } else {
        await setDoc(conversationRef, {
          messages,
          suggestions,
          code,
          createdAt: serverTimestamp(),
        });
      }
    } catch (error) {
      enqueueSnackbar('Сталася помилка! Спробуйте будь ласка пізніше.', {
        variant: 'error',
      });
    }
  };

  useEffect(() => {
    if (!uid || !conversationId || !conversationMessagesReceivedRef.current)
      return;

    updateConversation();
  }, [sessionId, uid, conversationId, messages, suggestions, code]);

  useEffect(() => {
    if (status.isRejected) setDialogOpen(true);
    if (status.isError)
      enqueueSnackbar('Сталася помилка! Спробуйте будь ласка пізніше.', {
        variant: 'error',
      });
  }, [status.isRejected, status.isError]);

  const handleChangeRequestDialogOpen = () => {
    setChangeRequestDialogOpen(true);
  };

  const handleChangeRequestDialogClose = () => {
    setChangeRequestDialogOpen(false);
  };

  if (conversation && conversation.empty) {
    return (
      <LoaderContainer>
        <CircularProgress />
      </LoaderContainer>
    );
  }

  return (
    <ChatPageContainer>
      {status.isPending && <Progress />}
      <AuthDialog open={dialogOpen} />
      <ChatsContainer maxWidth="xl">
        <Grid container spacing={{ md: 3, sm: 2, xs: 2 }} height={'100%'}>
          <GridContainer size={{ md: 6, sm: 12, xs: 12 }}>
            <MessagePanel label="Повідомлення від LLM" messages={messages} />
          </GridContainer>
          <GridContainer size={{ md: 6, sm: 12, xs: 12 }}>
            <ChangeRequestDialog
              open={changeRequestDialogOpen}
              onClose={handleChangeRequestDialogClose}
              code={code || ''}
            />
            <CodePanel
              label="Згенерований код"
              code={code}
              onChangeRequestClick={handleChangeRequestDialogOpen}
            />
          </GridContainer>
        </Grid>
      </ChatsContainer>
      <PromptToolbar
        onSubmit={sendPrompt}
        suggestions={suggestions}
        disabled={promptDisabled}
      />
    </ChatPageContainer>
  );
};

export default Chat;
